import * as defaultDb from "./db";
import { embedQuery } from "./embed";
import type { Hit, LegRow, SearchBackend } from "./types";

const RRF_K = 60;
const FAN_OUT = 4;

/** Reciprocal-rank-fusion score for a single ranked list (rank is 0-indexed). */
function rrf(rank: number): number {
  return 1 / (RRF_K + rank + 1);
}

/**
 * Hybrid retrieval: vector + FTS5, fused via RRF, then deduped by episode.
 *
 * Direct port of `search_hybrid` from podown/podown_query_demo.py — same
 * RRF_K, same fan-out (top_k * 4), same dedup-by-episode behavior.
 */
export async function searchHybrid(
  query: string,
  topK = 10,
  backend?: SearchBackend,
): Promise<Hit[]> {
  const trimmed = query.trim();
  if (!trimmed) return [];

  const realBackend: SearchBackend = backend ?? {
    vectorTopK: async (q, k) => defaultDb.vectorTopK(await embedQuery(q), k),
    ftsTopK: defaultDb.ftsTopK,
    hydrate: defaultDb.hydrate,
  };

  const fanOut = topK * FAN_OUT;
  const [vecRows, ftsRows] = await Promise.all([
    realBackend.vectorTopK(trimmed, fanOut),
    realBackend.ftsTopK(trimmed, fanOut),
  ]);

  const score = fuseRrf(vecRows, ftsRows);
  if (score.size === 0) return [];

  // Hydrate the candidate pool, then dedupe-and-truncate to topK.
  const ranked = [...score.entries()].sort((a, b) => b[1] - a[1]).slice(0, fanOut);
  const ids = ranked.map(([id]) => id);
  const docs = await realBackend.hydrate(ids);

  const scoreById = new Map(ranked);
  const candidates: Hit[] = docs
    .map((d) => ({ ...d, score: scoreById.get(d.id) ?? 0 }))
    .sort((a, b) => b.score - a.score);

  const seen = new Set<string>();
  const out: Hit[] = [];
  for (const h of candidates) {
    if (seen.has(h.episode)) continue;
    seen.add(h.episode);
    out.push(h);
    if (out.length >= topK) break;
  }
  return out;
}

/** Exposed for testing — fuse two ranked lists by reciprocal-rank fusion. */
export function fuseRrf(vec: readonly LegRow[], fts: readonly LegRow[]): Map<number, number> {
  const out = new Map<number, number>();
  vec.forEach(([id], rank) => out.set(id, (out.get(id) ?? 0) + rrf(rank)));
  fts.forEach(([id], rank) => out.set(id, (out.get(id) ?? 0) + rrf(rank)));
  return out;
}
